/**
 * Componente Logo Corporativo
 * Muestra el logo apropiado según el contexto (claro u oscuro)
 */

import React from 'react';
import { Box, BoxProps } from '@mui/material';
import { useTheme } from '@mui/material/styles';

interface LogoProps extends Omit<BoxProps, 'component'> {
  variant?: 'light' | 'dark' | 'auto';
  width?: number | string;
  height?: number | string;
  /**
   * Texto accesible para describir el logo
   */
  altText?: string;
  /**
   * Etiqueta aria opcional (para casos donde el logo es interactivo)
   */
  ariaLabel?: string;
}

/**
 * Componente Logo
 * @param variant - 'light' para fondos oscuros, 'dark' para fondos claros, 'auto' para detectar automáticamente
 * @param width - Ancho del logo (por defecto 150px)
 * @param height - Alto del logo (por defecto auto)
 */
export const Logo: React.FC<LogoProps> = ({
  variant = 'auto',
  width = 120,
  height = 'auto',
  altText = 'Logo corporativo de Cervellia Technologies',
  ariaLabel,
  sx,
  ...props
}) => {
  const theme = useTheme();
  
  // Determinar qué logo usar
  const resolvedVariant: 'light' | 'dark' =
    variant === 'auto'
      ? theme.palette.mode === 'dark'
        ? 'light'
        : 'dark'
      : variant;

  const getLogoPath = () => {
    if (resolvedVariant === 'light') {
      return theme.custom.logoPaths.light;
    }
    return theme.custom.logoPaths.dark;
  };

  const logoPath = getLogoPath();
  
  // Fallback a PNG si SVG no está disponible
  const logoPathPng = resolvedVariant === 'light' 
    ? theme.custom.logoPaths.lightPng 
    : theme.custom.logoPaths.darkPng;

  return (
    <Box
      component="img"
      src={logoPath}
      alt={altText}
      aria-label={ariaLabel}
      onError={(e) => {
        // Fallback a PNG si SVG falla
        const target = e.target as HTMLImageElement;
        if (target.src !== logoPathPng) {
          target.src = logoPathPng;
        }
      }}
      sx={{
        width,
        height,
        objectFit: 'contain',
        ...sx,
      }}
      {...props}
    />
  );
};

export default Logo;

